/**
 * Profile Language Editor Modal - Simplified Unified Version
 * All languages shown in a single list with chips
 */

import { getCountryCode as getCountryCodeFromMap } from './lang-country-map.js';
import { lockBodyScroll, unlockBodyScroll } from './scroll-lock.js';
import { getApiToken } from './tokenUtils.js';
import { clearJwtToken } from './jwtTokenManager.js';

const POPULAR_LANGS = ['EN', 'ES', 'FR', 'DE', 'PT', 'IT', 'ZH', 'JA', 'KO', 'RU', 'AR', 'HI'];
const MAX_TOTAL_LANGS = 10;
const MAX_NATIVE_LANGS = 3;

export function profileLanguageModal(config = {}) {
    return {
        // Modal state
        isOpen: false,

        // Unified language list: [{code, level, learning}]
        // level 4 = native, 0-3 = other proficiency levels
        allUserLangs: [],
        allLangs: config.allLangs || {},

        // Initial data passed from server
        initialNativeLangs: config.initialNativeLangs || [],
        initialOtherLangs: config.initialOtherLangs || [],
        endpoint: config.endpoint || '/v1/user-languages/self',
        format: config.format === 'account' ? 'account' : 'user-languages',

        // UI state
        mode: 'list',  // 'list', 'add'
        expandedIndex: -1,  // Which language is expanded for inline editing (-1 = none)
        searchQuery: '',

        // Pending new language (not yet confirmed)
        pendingLang: null,  // {code, level, learning}

        // Saving
        saving: false,
        saveMessage: '',
        saveError: false,

        // Native limit message (shown on-demand when user taps disabled native option)
        showNativeLimitMsg: false,

        // Constants
        maxTotalLangs: MAX_TOTAL_LANGS,
        maxNativeLangs: MAX_NATIVE_LANGS,

        // Level options for the edit form
        levelOptions: {
            4: { name: 'Native', desc: 'Mother tongue' },
            3: { name: 'Proficient', desc: 'Near-native' },
            2: { name: 'Advanced', desc: 'C1-C2' },
            1: { name: 'Intermediate', desc: 'B1-B2' },
            0: { name: 'Beginner', desc: 'A1-A2' }
        },

        // ========== COMPUTED ==========

        get canAddMoreLangs() {
            return this.allUserLangs.length < MAX_TOTAL_LANGS;
        },

        get nativeLangsCount() {
            return this.allUserLangs.filter(l => Number(l.level) === 4).length;
        },

        canSelectNative(currentLangLevel) {
            // Can select native if: already native, or under the limit
            return Number(currentLangLevel) === 4 || this.nativeLangsCount < MAX_NATIVE_LANGS;
        },

        isLastNativeLang(index) {
            // Check if the language at this index is the only native language
            if (index < 0 || index >= this.allUserLangs.length) return false;
            const lang = this.allUserLangs[index];
            return Number(lang.level) === 4 && this.nativeLangsCount === 1;
        },

        canChangeFromNative(index) {
            // Can only change from native if there's more than one native language
            if (index < 0 || index >= this.allUserLangs.length) return true;
            const lang = this.allUserLangs[index];
            return Number(lang.level) !== 4 || this.nativeLangsCount > 1;
        },

        flashNativeLimitMsg() {
            this.showNativeLimitMsg = true;
            setTimeout(() => { this.showNativeLimitMsg = false; }, 3000);
        },

        get isEditing() {
            return this.isOpen;
        },

        get popularLangs() {
            return POPULAR_LANGS
                .filter(code => this.allLangs[code])
                .map(code => ({
                    code,
                    name: this.allLangs[code],
                    countryCode: getCountryCodeFromMap(code)
                }));
        },

        get sortedLangs() {
            return Object.entries(this.allLangs)
                .map(([code, name]) => ({
                    code,
                    name,
                    countryCode: getCountryCodeFromMap(code)
                }))
                .sort((a, b) => a.name.localeCompare(b.name));
        },

        get filteredLangs() {
            const query = this.searchQuery.toLowerCase().trim();
            if (!query) return [];
            return this.sortedLangs.filter(l =>
                l.name.toLowerCase().includes(query) ||
                l.code.toLowerCase().includes(query)
            );
        },

        // Get the language currently being edited (existing or pending)
        get editingLang() {
            if (this.expandedIndex >= 0) {
                return this.allUserLangs[this.expandedIndex];
            }
            return this.pendingLang;
        },

        // Check if a language at index is expanded
        isExpanded(index) {
            return this.expandedIndex === index;
        },

        // ========== METHODS ==========

        init() {
            // Initialize allUserLangs from server data on page load
            this.initializeLanguages();

            this.$watch('isOpen', (value) => {
                if (value) {
                    lockBodyScroll();
                } else {
                    unlockBodyScroll();
                }
            });
        },

        initializeLanguages() {
            const nativeLangs = (this.initialNativeLangs || []).map(l => ({
                code: l.code || l.lang,
                level: 4,
                learning: false,
                countryCode: getCountryCodeFromMap(l.code || l.lang)
            }));

            const otherLangs = (this.initialOtherLangs || []).map(l => ({
                code: l.code || l.lang,
                level: l.level ?? 1,
                learning: l.learning ?? false,
                countryCode: getCountryCodeFromMap(l.code || l.lang)
            }));

            // Combine and sort by level descending
            this.allUserLangs = [...nativeLangs, ...otherLangs].sort((a, b) => b.level - a.level);
        },

        getCountryCode(code) {
            return getCountryCodeFromMap(code);
        },

        isLanguageAdded(code) {
            return this.allUserLangs.some(l => l.code === code);
        },

        // ========== MODAL CONTROL ==========

        open() {
            if (this.isOpen) return;

            // Reset UI state
            this.mode = 'list';
            this.expandedIndex = -1;
            this.searchQuery = '';
            this.pendingLang = null;
            this.isOpen = true;

            this.$nextTick(() => {
                if (typeof lucide !== 'undefined') {
                    lucide.createIcons();
                }
            });
        },

        close() {
            this.isOpen = false;
            this.mode = 'list';
            this.expandedIndex = -1;
            this.searchQuery = '';
            this.pendingLang = null;
        },

        // ========== UNIFIED LANGUAGE METHODS ==========

        openPicker() {
            this.mode = 'add';
            this.searchQuery = '';
            this.$nextTick(() => this.$refs.searchInput?.focus());
        },

        closePicker() {
            this.mode = 'list';
            this.searchQuery = '';
        },

        selectLanguageForAdd(code) {
            if (this.isLanguageAdded(code)) return;

            // Set as pending (not yet added to list) - no default level
            this.pendingLang = {
                code,
                level: null,
                learning: false,
                countryCode: getCountryCodeFromMap(code)
            };

            // Close picker and show pending in list
            this.mode = 'list';
            this.expandedIndex = -1;
            this.searchQuery = '';

            this.$nextTick(() => {
                if (typeof lucide !== 'undefined') {
                    lucide.createIcons();
                }
            });
        },

        toggleLanguageExpand(index) {
            // Toggle expansion - collapse if already expanded, expand if not
            this.expandedIndex = this.expandedIndex === index ? -1 : index;
        },

        setUnifiedLevel(index, level) {
            const lang = index === -1 ? this.pendingLang : this.allUserLangs[index];
            if (!lang) return;

            // Prevent changing from native to non-native if it's the last native
            if (index >= 0 && Number(lang.level) === 4 && level !== 4 && this.nativeLangsCount === 1) {
                return;
            }

            lang.level = level;
            // Native speakers don't need "learning" flag
            if (level === 4) {
                lang.learning = false;
            }
            // Auto-enable learning for beginners/intermediate
            else if (level <= 1) {
                lang.learning = true;
            }

            // Auto-save and collapse if editing existing language
            if (index >= 0) {
                this.sortLanguagesByLevel();
                this.expandedIndex = -1;
                this.autoSave();
            }
        },

        toggleUnifiedLearning(index) {
            const lang = index === -1 ? this.pendingLang : this.allUserLangs[index];
            if (!lang) return;

            // Don't allow practice flag for native languages
            if (Number(lang.level) === 4) return;

            lang.learning = !lang.learning;

            // Auto-save if editing existing language
            if (index >= 0) {
                this.autoSave();
            }
        },

        saveAndCollapse(index) {
            if (index >= 0) {
                this.sortLanguagesByLevel();
                this.autoSave();
                this.expandedIndex = -1;
            }
        },

        removeUnifiedLanguage(index) {
            // If removing a pending language, just cancel
            if (index === -1) {
                this.pendingLang = null;
                return;
            }

            // Prevent removing the last native language
            if (this.isLastNativeLang(index)) {
                return;
            }

            this.allUserLangs.splice(index, 1);
            this.expandedIndex = -1;
            this.autoSave();
        },

        cancelPending() {
            // Discard pending language
            this.pendingLang = null;
        },

        confirmPending() {
            // Add pending language to the list and save
            if (this.pendingLang && this.pendingLang.level !== null) {
                this.allUserLangs.push(this.pendingLang);
                this.pendingLang = null;
                this.sortLanguagesByLevel();
                this.autoSave();
            }
        },

        collapseAll() {
            this.expandedIndex = -1;
        },

        saveAllAndClose() {
            this.close();
        },

        sortLanguagesByLevel() {
            this.allUserLangs.sort((a, b) => b.level - a.level);
        },

        // ========== API SAVE ==========

        async autoSave() {
            // Abort any in-flight save to avoid duplicate/stale requests
            if (this._saveController) {
                this._saveController.abort();
            }
            this._saveController = new AbortController();

            this.saving = true;
            this.saveError = false;

            try {
                await this._doSave(false);

                this.saveMessage = 'Saved';
                setTimeout(() => { this.saveMessage = ''; }, 2000);
                this.$dispatch('languages-updated', { allUserLangs: this.allUserLangs });

            } catch (error) {
                if (error.name === 'AbortError') return;
                console.error('Error saving languages:', error);
                this.saveMessage = 'Failed to save';
                this.saveError = true;
                setTimeout(() => {
                    this.saveMessage = '';
                    this.saveError = false;
                }, 3000);
            } finally {
                this.saving = false;
            }
        },

        async _doSave(isRetry) {
            let apiToken = await getApiToken();
            if (!apiToken) {
                throw new Error('No API token found');
            }

            // Native speakers (level 4) always go to langs_known; otherwise
            // the practice flag decides which list a language belongs to.
            const langsKnown = this.allUserLangs
                .filter(l => Number(l.level) === 4 || !l.learning)
                .map(l => ({
                    lang: l.code.toUpperCase(),
                    level: parseInt(l.level)
                }));

            const langsLearning = this.allUserLangs
                .filter(l => Number(l.level) !== 4 && l.learning)
                .map(l => ({
                    lang: l.code.toUpperCase(),
                    level: parseInt(l.level)
                }));

            const body = new FormData();
            if (this.format === 'account') {
                body.append('know', langsKnown.map(l => `${l.lang}${l.level}`).join(','));
                body.append('learn', langsLearning.map(l => `${l.lang}${l.level}`).join(','));
            } else {
                body.append('langs_known', JSON.stringify(langsKnown));
                body.append('langs_learning', JSON.stringify(langsLearning));
            }

            const response = await fetch(this.endpoint, {
                method: 'PUT',
                body,
                headers: { 'X-AUTH-TOKEN': apiToken },
                credentials: 'include',
                signal: this._saveController.signal
            });

            if (response.status === 401 && !isRetry) {
                clearJwtToken();
                return this._doSave(true);
            }

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to save');
            }
        }
    };
}

export { POPULAR_LANGS, MAX_TOTAL_LANGS };
